import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import OurServiceOffer from '../../Components/Automation/OurServiceOffer'
import { ChevronRight } from 'lucide-react'
import Perspectives from '../../Components/Accelerating/Perspectives'
import RelatedServices from '../../Components/Accelerating/RelatedServices'
import AccContact from '../../Components/Accelerating/AccContact'
import Banner from '../../Components/Banner'
import { Helmet } from 'react-helmet-async'
import ContactSection from '../../Components/home/ContactSection'

const relatedService = [
    {
        id: 1,
        title: "Cloud & DevOps Engineering",
        description: "Speed up your software delivery with modern cloud and DevOps practices, while keeping your systems secure, scalable, and ready to grow with you.",
        image: "/assets/product/p1.png",
        link: "/cloud-dev-ops-engineering"
    },
    {
        id: 2,
        title: "Enterprise Applications & ERP",
        description: "Simplify your business operations and move forward digitally with reliable ERP solutions that make everyday work easier, more efficient, and ready for growth.",
        image: "/assets/product/p3.png",
        link: "/enterprise-applications-erp"
    },
    {
        id: 3,
        title: "Product Engineering & Digital",
        description: "End-to-end product development to help you move faster, build smarter, and create smooth, meaningful digital experiences your users will truly appreciate.",
        image: "/assets/product/p4.png",
        link: "/product-engineering-digital"
    },
    {
        id: 4,
        title: "Quality Engineering & Performance",
        description: "We make sure your software runs smoothly with careful testing, smart automation, and performance tuning so everything works reliably and feels seamless for users.",
        image: "/assets/product/p2.png",
        link: "/quality-engineering-performance"
    }
];

const data = [
    {
        id: 1,
        category: "GENERATIVE AI",
        title: "Reshaping Business Operations with Custom LLMs and GenAI",
        link: "#"
    },
    {
        id: 2,
        category: "INTELLIGENT AUTOMATION",
        title: "Beyond RPA: Why Autonomous Agents are the Next Big Leap in Efficiency",
        link: "#"
    },
    {
        id: 3,
        category: "PREDICTIVE ANALYTICS",
        title: "From Reactive to Proactive: Leveraging Data for Smarter Decision Making",
        link: "#"
    },
    {
        id: 4,
        category: "AI INFRASTRUCTURE",
        title: "Building a Scalable Cloud Foundation for High-Performance AI Models",
        link: "#"
    }
];

const service = [
    {
        id: 1,
        title: "Agentic AI Solutions",
        category: "service",
        desc: "Deploy smart AI agents that can plan, handle, and improve complex workflows on their own, reducing manual work while boosting efficiency and results.",
        defaultBg: "bg-gradient-to-b from-[#01386A] to-[#1C355E]",
        link: "/agentic-ai-services",
    },
    {
        id: 2,
        title: "AI-driven Recruitment Automation",
        category: "service",
        desc: "Make hiring easier with AI that screens resumes, matches the right candidates, and handles scheduling, so you can find great talent faster.",
        defaultBg: "bg-gradient-to-r from-[#01386A] to-[#4B6CB7]",
        link: "/ai-automation",
    },
    {
        id: 3,
        title: "Generative AI & LLM Integrations",
        category: "service",
        desc: "Use powerful language models to create content, write code, and uncover insights that truly fit your business and its data.",
        defaultBg: "bg-gradient-to-br from-[#01386A] to-[#8E9EAB]",
        link: "/",
    },
    {
        id: 4,
        title: "Process Automation (RPA + AI)",
        category: "service",
        desc: "Blend RPA with AI to simplify processes, reduce errors, and make everyday operations smoother and cost-effective.",
        defaultBg: "bg-gradient-to-bl from-[#01386A] to-[#6A85B6]",
        link: "/",
    },
    {
        id: 5,
        title: "Predictive Analytics & Data Intelligence",
        category: "service",
        desc: "Turn your data into clear insights and spot future trends early, so you can make smarter decisions with confidence and grow sustainably.",
        defaultBg: "bg-gradient-to-tr from-[#01386A] to-[#253B5C]",
        link: "/data-analytics",
    },
    {
        id: 6,
        title: "AI Chatbots & Enterprise Assistants",
        category: "service",
        desc: "Make customer support smoother and help your team stay productive with smart, always-available AI assistants.",
        defaultBg: "bg-gradient-to-t from-[#01386A] to-[#243B55]",
        link: "/",
    },
]

function AIIntelligentAutomation() {
    return (
        <div>
            <Helmet>
    <title>
        AI & Intelligent Automation Services | AI Solutions | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode delivers AI and intelligent automation solutions that help businesses automate workflows, deploy AI agents, integrate generative AI, improve decision-making, and drive operational efficiency."
    />

    <meta
        name="keywords"
        content="AI Intelligent Automation, AI Automation Services, Intelligent Automation Solutions, AI Automation Company, Agentic AI Solutions, Generative AI Solutions, AI Workflow Automation, AI Business Automation, AI Process Automation, Enterprise AI Solutions, AI Consulting Services, Intelligent Process Automation"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/capabilities/ai-automation"
    />

    <meta
        property="og:title"
        content="AI & Intelligent Automation Services | Mobikode"
    />

    <meta
        property="og:description"
        content="Transform business operations with Mobikode's AI and intelligent automation solutions. From Agentic AI and Generative AI to workflow automation and predictive analytics, build smarter and more efficient processes."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/capabilities/ai-automation"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/what/c1.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="AI & Intelligent Automation Services | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Explore Mobikode's AI automation solutions including Agentic AI, Generative AI, RPA, predictive analytics, and enterprise AI assistants designed to improve efficiency and accelerate digital transformation."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/what/c1.png"
    />
</Helmet>
            <AiHero title="AI & Intelligent Automation" imgData="/assets/what/c1.png" />
            <section className="w-full py-[50px] lg:py-[80px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
                    {/* Changed: flex-col for mobile, lg:flex-row for desktop. Height auto for mobile. */}
                    <div className="relative w-full flex flex-col lg:flex-row h-auto lg:h-[323px] group">

                        {/* Image Container */}
                        {/* Changed: Fixed height on mobile (250px), full height on desktop */}
                        <div className="overflow-hidden w-full lg:max-w-[624px] h-[250px] lg:h-full relative z-10">
                            <img
                                src="/assets/what/item16.png"
                                alt="ai automation"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>

                        <div className="relative z-20 w-full flex justify-center items-center lg:max-w-[656px] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] 
                            px-[20px] py-[30px] lg:px-[40px] lg:py-[30px] 
                            -mt-6 lg:mt-0 lg:ml-0 group">

                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                At Mobikode, we help businesses simplify the way they work using AI and automation. From reducing manual tasks to improving decision-making, our solutions make everyday processes smoother and more efficient. The goal is simple: help you save time, work smarter, and grow with confidence.

                            </p>
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>
                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>

                        </div>

                    </div>
                </div>
            </section>
            <OurServiceOffer service={service} />
            {/* <section className='overflow-hidden'>
                <div className="max-w-[1440px] lg:pb-[80px] pb-[40px] w-full mx-auto lg:px-[80px] px-[20px]">
                    <h6 className="text-center font-[600] lg:text-[31px] text-[20px] lg:pb-[80px] pb-[40px] lg:leading-[40px] leading-[30px] text-[#01386A]">Spotlight</h6>
                    <div className="flex lg:flex-row flex-col gap-5">
                        <div className="w-full lg:w-[65%] mx-auto relative group">
                            <div className="relative w-full h-[350px] lg:h-auto lg:aspect-auto">
                                <img src="/assets/what/img6.png" className="w-full h-full object-cover" alt="" />
                                <div className="absolute top-0 left-0 w-full h-full bg-[#00000033]"></div>
                                <div className="absolute top-0 left-0 p-[20px] lg:p-[40px] w-full h-full flex flex-col justify-between">
                                    <p className="font-[500] font-graphik text-[16px] lg:text-[18px] leading-[24px] text-[#FFFFFF]">CASE STUDY</p>
                                    <div>
                                        <h6 className="font-[500] font-graphik text-[20px] lg:text-[24px] leading-[26px] lg:leading-[30px] text-[#FFFFFF] max-w-[666px] w-full pb-[20px]">Deploying an automated collaborative platform to improve PDI scoring for a fortune 500 manufacturer</h6>
                                        <div className="flex items-center gap-2 font-graphik font-[500] text-[16px] text-[#FFFFFF]">
                                            <span>Read More</span>
                                            <ChevronRight size={20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="w-full lg:w-[35%] mx-auto grid gap-[20px]">
                            <div className="relative h-[300px] lg:h-auto">
                                <img src="/assets/what/img5.png" className="w-full h-full object-cover" alt="" />
                                <div className="absolute top-0 left-0 w-full h-full bg-[#00000033]"></div>
                                <div className="absolute top-0 left-0 p-[20px] lg:p-[40px] w-full h-full flex flex-col justify-between">
                                    <p className="font-[500] font-graphik text-[16px] lg:text-[18px] leading-[24px] text-[#FFFFFF]">CASE STUDY</p>
                                    <div>
                                        <h6 className="font-[500] font-graphik text-[20px] lg:text-[24px] leading-[26px] lg:leading-[30px] text-[#FFFFFF] max-w-[666px] w-full pb-[20px] line-clamp-2">
                                            Deploying an automated collaborative platform to .....
                                        </h6>
                                        <div className="flex items-center gap-2 font-graphik font-[500] text-[16px] text-[#FFFFFF]">
                                            <span>Read More</span>
                                            <ChevronRight size={20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="relative h-[300px] lg:h-auto">
                                <img src="/assets/what/img7.png" className="w-full h-full object-cover" alt="" />
                                <div className="absolute top-0 left-0 w-full h-full bg-[#00000033]"></div>
                                <div className="absolute top-0 left-0 p-[20px] lg:p-[40px] w-full h-full flex flex-col justify-between">
                                    <p className="font-[500] font-graphik text-[16px] lg:text-[18px] leading-[24px] text-[#FFFFFF]">CASE STUDY</p>
                                    <div>
                                        <h6 className="font-[500] font-graphik text-[20px] lg:text-[24px] leading-[26px] lg:leading-[30px] text-[#FFFFFF] max-w-[666px] w-full pb-[20px] line-clamp-2">
                                            Deploying an automated collaborative platform to .....
                                        </h6>
                                        <div className="flex items-center gap-2 font-graphik font-[500] text-[16px] text-[#FFFFFF]">
                                            <span>Read More</span>
                                            <ChevronRight size={20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section> */}
            <section className="w-full pb-[40px] lg:pb-[80px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px]">
                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/what/img8.png"
                                alt="AI & Intelligent Automation"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">
                            <h3 className="text-[#01386A] text-[20px] lg:text-[30px] font-[600] font-sora mb-[15px] lg:mb-[35px] leading-[1.3]">
                                AI & Intelligent Automation Mobikode
                            </h3>
                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">

                                At Mobikode, we help businesses move beyond traditional ways of working using AI and intelligent automation. We turn everyday processes into smarter, more adaptive systems that think and improve over time, helping you make better decisions, reduce costs, and create more meaningful, personalized experiences for your customers.
                            </p>
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>
                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>
                        </div>
                    </div>
                </div>
            </section>
            {/* <section>
                <div className="max-w-[1440px] w-full mx-auto lg:pl-[80px] pl-[20px]">
                    <h6 className="font-[600] font-sora lg:text-[31px] text-[20px] leading-[40px] text-[#01386A] text-lg:center">Mobikode Perspectives leading-[30px]</h6>
                </div>
            </section> */}
            {/* <Perspectives data={data} /> */}
            <RelatedServices relatedService={relatedService} />
            {/* <section className='relative'>
                <img src="/assets/product/bg1.jpg" className='lg:h-[683px] h-[800px] w-full object-cover' alt="" />
                <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px] top-1/2 -translate-y-1/2 absolute left-1/2 -translate-x-1/2">
                    <AccContact />
                </div>
            </section> */}
            <Banner title="AI & Intelligent Automation" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
            <ContactSection/>
        </div>
    )
}

export default AIIntelligentAutomation
